import type { ProjectStatus } from "./field-map.js";

/** RFC 2.2 Active Project rule - project summary shape required for selection. */
export interface ProjectListItem {
  readonly id: string;
  readonly status: ProjectStatus;
  readonly name: string;
  readonly plannedCompletion: string | null;
  readonly ownerMembershipId: string;
  readonly updatedAt: string;
}

export type ActiveProjectResolution =
  | { readonly kind: "linked"; readonly projectId: string }
  | { readonly kind: "selected"; readonly project: ProjectListItem }
  | { readonly kind: "chooser"; readonly candidates: readonly ProjectListItem[] }
  | { readonly kind: "empty" }
  | { readonly kind: "unavailable"; readonly projectId: string };

const TIERS: readonly ProjectStatus[] = ["IN_PROGRESS", "PLANNED", "DRAFT"];

/**
 * RFC 2.2 - deterministic Active Project selection.
 * Caller loads linked project via GET when kind === "linked".
 * When website.projectId is null, pass the **full** non-archived client project list
 * (exhaust pagination - Delta P2-D1).
 */
export const resolveActiveProject = (
  websiteProjectId: string | null,
  clientProjects: readonly ProjectListItem[],
): ActiveProjectResolution => {
  if (websiteProjectId !== null) {
    return { kind: "linked", projectId: websiteProjectId };
  }

  for (const status of TIERS) {
    const tier = clientProjects.filter((project) => project.status === status);
    if (tier.length === 1) {
      const project = tier[0];
      if (project === undefined) {
        continue;
      }
      return { kind: "selected", project };
    }
    if (tier.length >= 2) {
      return { kind: "chooser", candidates: tier };
    }
  }

  return { kind: "empty" };
};

export const activeProjectUnavailable = (projectId: string): ActiveProjectResolution => ({
  kind: "unavailable",
  projectId,
});
